/**
 * OrdersTab.js — Cycle order aggregate, CSV export, individual order management
 * Extracted from AdminDashboard.js during Phase 1 refactor
 */

import React, { useState, useEffect, useCallback } from "react";
import S from "./adminStyles";
import { ORDERS_API, CYCLES_API } from "./adminConstants";
import { exportToCSV } from "../../utils/exportUtils";
import QRCode from 'qrcode';

export default function OrdersTab({ user }) {
  const [cycleId, setCycleId] = useState("");
  const [cycles, setCycles] = useState([]);
  const [aggregate, setAggregate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [cycleLoading, setCycleLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [printPanelId, setPrintPanelId]   = useState(null);
  const [printBagCount, setPrintBagCount] = useState(1);

  // Load all cycles on mount, default to the open one
  useEffect(() => {
    const loadCycles = async () => {
      try {
        const token = await user.getIdToken();
        const res = await fetch(CYCLES_API, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const sorted = (data.cycles || []).sort((a, b) =>
            b.cycleId.localeCompare(a.cycleId)
          );
          setCycles(sorted);
          const open = sorted.find((c) => c.status === "open");
          setCycleId((open || sorted[0])?.cycleId || "");
        }
      } catch (err) {
        console.error("Cycles load error:", err);
      } finally {
        setCycleLoading(false);
      }
    };
    loadCycles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAggregate = useCallback(async () => {
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${ORDERS_API}/aggregate?cycleId=${cycleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAggregate(data.aggregates || {});
      }
    } catch (err) {
      console.error("Aggregate load error:", err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId]);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${ORDERS_API}/list?cycleId=${cycleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      }
    } catch (err) {
      console.error("Orders list load error:", err);
    } finally {
      setOrdersLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId]);

  useEffect(() => {
    if (cycleId && !cycleLoading) {
      loadAggregate();
      loadOrders();
    }
  }, [loadAggregate, loadOrders, cycleId, cycleLoading]);

  const updateOrderStatus = async (orderId, newStatus) => {
    setStatusUpdating(orderId);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${ORDERS_API}/${orderId}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        setOrders((prev) =>
          prev.map((o) =>
            o.orderId === orderId
              ? {
                  ...o,
                  status: newStatus,
                  statusUpdatedAt: new Date().toISOString(),
                  statusUpdatedBy: user.email,
                }
              : o,
          ),
        );
      }
    } catch (err) {
      console.error("Status update error:", err);
    } finally {
      setStatusUpdating(null);
    }
  };

  const exportCSV = (supplierId) => {
    if (!aggregate) return;
    const suppliers = supplierId
      ? { [supplierId]: aggregate[supplierId] }
      : aggregate;
    const rows = [
      [
        "Supplier",
        "Item",
        "Item ID",
        "Total Units",
        "Cases Needed",
        "Kroger SKU",
      ],
    ];
    Object.entries(suppliers).forEach(([sid, sData]) => {
      (sData.items || []).forEach((item) => {
        rows.push([
          sData.supplierName || sid,
          item.name || item.itemId,
          item.itemId,
          item.totalUnits,
          item.casesNeeded,
          item.krogerSku || "",
        ]);
      });
    });
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `raider-depot-${supplierId || "all"}-${cycleId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportOrdersCSV = () => {
    const cols = [
      { key: "tokenId", label: "Token ID" },
      { key: "status", label: "Status" },
      { key: "itemCount", label: "Item Count" },
      { key: "totalQty", label: "Total Qty" },
      { key: "submittedAt", label: "Submitted At" },
      { key: "statusUpdatedBy", label: "Status Updated By" },
    ];
    const rows = orders.map((o) => ({
      ...o,
      itemCount: o.items?.length ?? 0,
      totalQty: (o.items || []).reduce((sum, item) => sum + (item.qty || 0), 0),
      submittedAt: o.submittedAt ? new Date(o.submittedAt).toISOString() : "",
    }));
    exportToCSV(`raider-depot-orders-${cycleId}.csv`, rows, cols);
  };

  const printSheet = async (order, bagCount = 1) => {
    const cycle = cycles.find((c) => c.cycleId === cycleId);
    const qrDataUrl = await QRCode.toDataURL(
      `https://riverwoodraiderdepot.org/order/${order.tokenId}`,
      { width: 160, margin: 1 }
    );

    const fmtDateTime = (iso) => {
      if (!iso) return "—";
      return new Date(iso).toLocaleDateString("en-US", {
        month: "long", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    };
    const fmtFullDate = (iso) => {
      if (!iso) return "—";
      return new Date(iso).toLocaleDateString("en-US", {
        weekday: "long", month: "long", day: "numeric", year: "numeric",
      });
    };

    const rawPickup = cycle?.pickupDate || cycle?.cycleId;
    const pickupIso = rawPickup?.toDate
      ? rawPickup.toDate().toISOString()
      : rawPickup?.seconds
      ? new Date(rawPickup.seconds * 1000).toISOString()
      : rawPickup;
    const pickupStr = fmtFullDate(pickupIso);

    const itemRows = (order.items || [])
      .map((item) => `<tr>
        <td style="padding:3px 8px;border:1px solid #e2e8f0;font-size:11px;">${item.name}</td>
        <td style="padding:3px 8px;border:1px solid #e2e8f0;font-size:11px;text-align:center;font-weight:600;">${item.qty}</td>
      </tr>`)
      .join("");

    const renderSheet = (bagNum) => `
<div style="page-break-after:${bagNum < bagCount ? "always" : "auto"};break-after:${bagNum < bagCount ? "page" : "auto"};padding:0.25in;font-family:Arial,sans-serif;max-width:8.5in;box-sizing:border-box;">

  <div style="background:#EFF6FF;border:2px solid #1E3A5F;border-radius:10px;display:flex;align-items:center;gap:16px;padding:16px 20px;margin-bottom:14px;">
    <img src="${qrDataUrl}" alt="QR Code" width="120" height="120" style="border:1px solid #cbd5e1;border-radius:4px;flex-shrink:0;" />
    <div style="flex:1;text-align:center;">
      <div style="font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;">Your Order Token</div>
      <div style="font-size:48px;font-weight:700;color:#1A3B6E;letter-spacing:2px;line-height:1.1;">${order.tokenId}</div>
      <div style="font-size:24px;font-weight:700;color:#374151;margin-top:6px;letter-spacing:1px;">Bag ${bagNum} of ${bagCount}</div>
    </div>
  </div>

  <table style="width:100%;border-collapse:collapse;margin-bottom:14px;font-size:12px;">
    <tbody><tr>
      <td style="padding:4px 8px;color:#6b7280;width:33%;">Order placed</td>
      <td style="padding:4px 8px;color:#6b7280;width:33%;">Cycle</td>
      <td style="padding:4px 8px;color:#6b7280;width:33%;">Scheduled pickup</td>
    </tr><tr>
      <td style="padding:4px 8px;font-weight:600;color:#1e293b;">${fmtDateTime(order.submittedAt)}</td>
      <td style="padding:4px 8px;font-weight:600;color:#1e293b;">${cycleId}</td>
      <td style="padding:4px 8px;font-weight:600;color:#1e293b;">${pickupStr}</td>
    </tr></tbody>
  </table>

  <table style="width:100%;border-collapse:collapse;margin-bottom:14px;">
    <thead><tr>
      <th style="background:#F0FDF4;padding:3px 8px;border:1px solid #e2e8f0;text-align:left;font-size:11px;color:#374151;">Item</th>
      <th style="background:#F0FDF4;padding:3px 8px;border:1px solid #e2e8f0;text-align:center;font-size:11px;color:#374151;width:50px;">Qty</th>
    </tr></thead>
    <tbody>${itemRows}</tbody>
  </table>

  <div style="background:#FEF3C7;border-radius:6px;padding:8px 12px;margin-bottom:10px;">
    <div style="font-weight:700;color:#92400e;font-size:12px;margin-bottom:2px;">Please return your empty bags!</div>
    <div style="font-size:11px;color:#78350f;line-height:1.4;">When you pick up your next order, please bring back any empty shopping bags from this order. Returned bags help us serve more families. Thank you!</div>
  </div>

  <div style="font-size:11px;color:#374151;line-height:1.6;">
    <span style="margin-right:16px;"><strong>Register:</strong> Visit riverwoodraiderdepot.org and scan your QR card.</span>
    <span><strong>Feedback:</strong> riverwoodraiderdepot.org/feedback</span>
  </div>

</div>`;

    const sheets = Array.from({ length: bagCount }, (_, i) => renderSheet(i + 1)).join("");

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Pickup Sheet — ${order.tokenId}</title>
<style>
  @page { size: letter portrait; margin: 0.15in; }
  * { box-sizing: border-box; }
  body { margin: 0; padding: 0; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style>
</head>
<body>${sheets}</body>
</html>`;

    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.onload = () => win.print();
  };

  const printAllSubmitted = () => {
    const submitted = orders.filter((o) => o.status === "submitted");
    if (submitted.length === 0) return;

    const cycle = cycles.find((c) => c.cycleId === cycleId);

    const fmtFullDate = (iso) => {
      if (!iso) return "—";
      return new Date(iso).toLocaleDateString("en-US", {
        weekday: "long", month: "long", day: "numeric", year: "numeric",
      });
    };

    const rawPickup = cycle?.pickupDate || cycle?.cycleId;
    const pickupIso = rawPickup?.toDate
      ? rawPickup.toDate().toISOString()
      : rawPickup?.seconds
      ? new Date(rawPickup.seconds * 1000).toISOString()
      : rawPickup;
    const pickupStr = fmtFullDate(pickupIso);

    const todayStr = new Date().toLocaleDateString("en-US", {
      month: "long", day: "numeric", year: "numeric",
    });

    const orderSections = submitted.map((order) => {
      const itemRows = (order.items || [])
        .map((item) => `
          <tr>
            <td style="padding:6px 10px;border:1px solid #e2e8f0;font-size:13px;">${item.name}</td>
            <td style="padding:6px 10px;border:1px solid #e2e8f0;font-size:13px;text-align:center;font-weight:600;">${item.qty}</td>
          </tr>`)
        .join("");

      return `
        <div style="page-break-inside:avoid;break-inside:avoid;margin-bottom:20px;padding-bottom:16px;border-bottom:1px solid #e2e8f0;">
          <div style="display:flex;align-items:center;gap:12px;margin-bottom:10px;">
            <span style="font-size:36px;font-weight:700;color:#1A3B6E;letter-spacing:2px;">${order.tokenId}</span>
            <span style="background:#dbeafe;color:#1d4ed8;font-size:11px;font-weight:700;padding:3px 8px;border-radius:4px;letter-spacing:0.5px;">SUBMITTED</span>
          </div>
          <table style="width:100%;border-collapse:collapse;">
            <thead><tr>
              <th style="background:#f8fafc;padding:6px 10px;border:1px solid #e2e8f0;text-align:left;font-size:12px;color:#374151;">Item</th>
              <th style="background:#f8fafc;padding:6px 10px;border:1px solid #e2e8f0;text-align:center;font-size:12px;color:#374151;width:70px;">Qty</th>
            </tr></thead>
            <tbody>${itemRows}</tbody>
          </table>
        </div>`;
    }).join("");

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Pack List — ${cycleId}</title>
<style>
  @page { size: letter portrait; margin: 0.5in; }
  * { box-sizing: border-box; }
  body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style>
</head>
<body>
  <div style="padding-bottom:12px;border-bottom:2px solid #1A3B6E;margin-bottom:6px;display:flex;justify-content:space-between;align-items:baseline;">
    <span style="font-size:22px;font-weight:700;color:#1A3B6E;">RAIDER DEPOT &mdash; Pack List</span>
    <span style="font-size:14px;color:#374151;">Pickup: ${pickupStr}</span>
  </div>
  <div style="font-size:12px;color:#94a3b8;margin-bottom:24px;">
    Cycle: ${cycleId} &middot; ${submitted.length} order${submitted.length !== 1 ? "s" : ""} to pack &middot; Printed ${todayStr}
  </div>
  ${orderSections}
</body>
</html>`;

    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.onload = () => win.print();
  };

  const STATUS_OPTIONS = [
    "submitted",
    "packing",
    "packed",
    "ready_for_pickup",
    "picked_up",
    "not_picked_up",
    "returned_to_inventory",
    "cancelled",
    "flagged",
    "pending_fulfillment",
    "fulfilled",
  ];
  const STATUS_COLORS = {
    submitted: "#3b82f6",
    packing: "#6366f1",
    packed: "#8b5cf6",
    ready_for_pickup: "#f59e0b",
    picked_up: "#16a34a",
    not_picked_up: "#dc2626",
    returned_to_inventory: "#64748b",
    cancelled: "#94a3b8",
    flagged: "#f59e0b",
    pending_fulfillment: "#f97316",
    fulfilled: "#059669",
  };

  const progressCounts = React.useMemo(() => {
    const counts = { submitted: 0, packing: 0, packed: 0, flagged: 0, picked_up: 0, cancelled: 0, other: 0 };
    orders.forEach((o) => {
      if (o.status === "submitted") counts.submitted++;
      else if (o.status === "packing") counts.packing++;
      else if (o.status === "packed") counts.packed++;
      else if (o.status === "flagged") counts.flagged++;
      else if (o.status === "picked_up") counts.picked_up++;
      else if (o.status === "cancelled") counts.cancelled++;
      else counts.other++;
    });
    return counts;
  }, [orders]);

  const filteredOrders = statusFilter === "all"
    ? orders
    : orders.filter((o) => o.status === statusFilter);

  const handleCancelPrint = useCallback(() => { setPrintPanelId(null); setPrintBagCount(1); }, []);

  useEffect(() => {
    if (!printPanelId) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleCancelPrint();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [printPanelId, handleCancelPrint]);

  return (
    <>
      {cycleLoading ? (
        <div style={S.card}>
          <p style={{ color: "#64748b" }}>Loading active cycle...</p>
        </div>
      ) : (
        <>
      {/* Cycle Info Bar */}
      {(() => {
        const cycle = cycles.find((c) => c.cycleId === cycleId);
        if (!cycle) return null;

        const fmtInfoDate = (raw) => {
          if (!raw) return "—";
          const iso = raw?.toDate
            ? raw.toDate().toISOString()
            : raw?.seconds
            ? new Date(raw.seconds * 1000).toISOString()
            : raw;
          return new Date(iso).toLocaleDateString("en-US", {
            month: "short", day: "numeric", year: "numeric",
          });
        };

        return (
          <div style={{
            background: "#F8FAFC",
            border: "1px solid #E2E8F0",
            borderRadius: "10px",
            padding: "12px 16px",
            display: "flex",
            gap: "16px",
          }}>
            {[
              { label: "Cycle", value: cycle.cycleId },
              { label: "Orders open", value: fmtInfoDate(cycle.openDate) },
              { label: "Orders close", value: fmtInfoDate(cycle.closeDate) },
              { label: "Pickup", value: fmtInfoDate(cycle.pickupDate) },
            ].map(({ label, value }) => (
              <div key={label} style={{ flex: 1 }}>
                <div style={{
                  fontSize: "11px",
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "4px",
                }}>
                  {label}
                </div>
                <div style={{ fontSize: "14px", fontWeight: "600", color: "#1A3B6E" }}>
                  {value}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Fulfillment Progress */}
      {orders.length > 0 && (
        <div style={{ ...S.card, marginTop: "24px" }}>
          <h2 style={S.cardTitle}>Fulfillment Progress</h2>
          <div style={{
            display: "flex",
            height: "28px",
            borderRadius: "6px",
            overflow: "hidden",
            background: "#f1f5f9",
            marginBottom: "16px",
          }}>
            {[
              { key: "submitted", label: "Submitted", color: "#3b82f6" },
              { key: "packing", label: "Packing", color: "#6366f1" },
              { key: "packed", label: "Packed", color: "#8b5cf6" },
              { key: "flagged", label: "Flagged", color: "#f59e0b" },
              { key: "picked_up", label: "Picked Up", color: "#16a34a" },
              { key: "cancelled", label: "Cancelled", color: "#94a3b8" },
              { key: "other", label: "Other", color: "#94a3b8" },
            ].map(({ key, label, color }) => {
              const count = progressCounts[key];
              if (count === 0) return null;
              const pct = (count / orders.length) * 100;
              return (
                <div
                  key={key}
                  title={`${label}: ${count}`}
                  style={{
                    width: `${pct}%`,
                    background: color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {pct >= 8 && (
                    <span style={{ fontSize: "11px", color: "#fff", fontWeight: "600" }}>
                      {count}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            {[
              { label: "Awaiting Pack", key: "submitted", color: "#3b82f6", bg: "#eff6ff" },
              { label: "Packing", key: "packing", color: "#6366f1", bg: "#eef2ff" },
              { label: "Packed", key: "packed", color: "#8b5cf6", bg: "#f5f3ff" },
              { label: "Flagged", key: "flagged", color: "#f59e0b", bg: "#fffbeb" },
              { label: "Picked Up", key: "picked_up", color: "#16a34a", bg: "#f0fdf4" },
              { label: "Cancelled", key: "cancelled", color: "#94a3b8", bg: "#f8fafc" },
            ].map(({ label, key, color, bg }) => (
              <div key={key} style={{
                background: bg,
                border: `1px solid ${color}33`,
                borderRadius: "8px",
                padding: "10px 16px",
                minWidth: "90px",
                textAlign: "center",
              }}>
                <div style={{ fontSize: "22px", fontWeight: "700", color }}>
                  {progressCounts[key]}
                </div>
                <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Individual Orders */}
      <div style={{ marginTop: "24px" }}>
        <div style={{ ...S.flexRowBetween, marginBottom: "12px" }}>
          <h2 style={S.cardTitle}>Individual Orders</h2>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {orders.some((o) => o.status === "submitted") && (
              <button
                style={{ ...S.btnSecondary, padding: "4px 12px", fontSize: "13px" }}
                onClick={printAllSubmitted}
              >
                Print Pack List
              </button>
            )}
            <select
              style={{ ...S.select, width: "auto", fontSize: "13px" }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="submitted">Submitted</option>
              <option value="packing">Packing</option>
              <option value="packed">Packed</option>
              <option value="flagged">Flagged</option>
              <option value="picked_up">Picked Up</option>
              <option value="not_picked_up">Not Picked Up</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
        {ordersLoading ? (
          <div style={S.card}>
            <p style={{ color: "#64748b" }}>Loading orders...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div style={S.card}>
            <p style={{ color: "#94a3b8", fontSize: "13px" }}>
              {statusFilter !== "all"
                ? "No orders match this filter."
                : "No orders found for this cycle."}
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {filteredOrders.map((order) => {
              const statusColor = STATUS_COLORS[order.status] || "#64748b";
              const showPackedDate = [
                "packed", "ready_for_pickup", "picked_up",
                "not_picked_up", "returned_to_inventory", "fulfilled",
              ].includes(order.status);
              const packedLabel =
                order.status === "picked_up" ? "Picked up" :
                order.status === "fulfilled" ? "Fulfilled" : "Packed";
              return (
                <div
                  key={order.orderId}
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                    borderLeft: `4px solid ${statusColor}`,
                    opacity: statusUpdating === order.orderId ? 0.6 : 1,
                    transition: "opacity 0.15s",
                  }}
                >
                  <div style={{ padding: "10px 14px" }}>
                    {/* Token ID + status dropdown */}
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "10px",
                    }}>
                      <div style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: "#1A3B6E",
                        letterSpacing: "1.5px",
                        lineHeight: 1,
                      }}>
                        {order.tokenId}
                      </div>
                      <select
                        style={{
                          ...S.select,
                          fontWeight: "600",
                          fontSize: "12px",
                          color: statusColor,
                          borderColor: statusColor,
                          width: "auto",
                          flexShrink: 0,
                        }}
                        value={order.status}
                        onChange={(e) => updateOrderStatus(order.orderId, e.target.value)}
                        disabled={statusUpdating === order.orderId}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                        ))}
                      </select>
                    </div>

                    {/* Flag reason banner */}
                    {order.status === "flagged" && (
                      <div style={{
                        marginBottom: "10px",
                        fontSize: "13px",
                        color: "#92400e",
                        background: "#fef3c7",
                        borderRadius: "4px",
                        padding: "4px 8px",
                      }}>
                        {order.flagReason || "Flagged by volunteer"}
                        {order.flaggedBy && (
                          <span style={{ color: "#b45309", marginLeft: "4px" }}>
                            · {order.flaggedBy}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Item list */}
                    <div style={{ marginBottom: "10px" }}>
                      {(order.items || []).map((item, i) => (
                        <div key={i} style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "19px",
                          color: "#475569",
                          padding: "2px 0",
                        }}>
                          <span>{item.name}</span>
                          <span style={{ fontWeight: "600", color: "#1e293b" }}>×{item.qty}</span>
                        </div>
                      ))}
                    </div>

                    {/* Meta row */}
                    <div style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "10px",
                      fontSize: "13px",
                      color: "#94a3b8",
                      borderTop: "1px solid #f1f5f9",
                      paddingTop: "8px",
                    }}>
                      {order.submittedAt && (
                        <span>
                          Submitted{" "}
                          {new Date(order.submittedAt).toLocaleDateString("en-US", {
                            month: "short", day: "numeric",
                            hour: "numeric", minute: "2-digit",
                          })}
                        </span>
                      )}
                      {showPackedDate && order.statusUpdatedAt && (
                        <span>
                          {packedLabel}{" "}
                          {new Date(order.statusUpdatedAt).toLocaleDateString("en-US", {
                            month: "short", day: "numeric",
                            hour: "numeric", minute: "2-digit",
                          })}
                        </span>
                      )}
                      {order.statusUpdatedBy && (
                        <span>by {order.statusUpdatedBy}</span>
                      )}
                    </div>

                    {/* Print Pickup Sheet */}
                    <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: "8px", marginTop: "8px" }}>
                      {printPanelId === order.orderId ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                          <span style={{ fontSize: "13px", color: "#64748b" }}>Bags:</span>
                          <button
                            style={{ width: 36, height: 36, borderRadius: 8, border: "1.5px solid #cbd5e1", background: "#FFF", fontSize: 20, fontWeight: 700, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#1A3B6E" }}
                            onClick={() => setPrintBagCount(Math.max(1, (parseInt(printBagCount) || 1) - 1))}
                          >
                            −
                          </button>
                          <input
                            type="number"
                            inputMode="numeric"
                            min="1"
                            max="10"
                            value={printBagCount}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === "") { setPrintBagCount(""); return; }
                              const n = parseInt(v, 10);
                              if (!isNaN(n)) setPrintBagCount(Math.max(1, Math.min(10, n)));
                            }}
                            onBlur={() => { if (printBagCount === "" || printBagCount < 1) setPrintBagCount(1); }}
                            style={{
                              width: "56px",
                              padding: "4px 8px",
                              border: "1px solid #cbd5e1",
                              borderRadius: "6px",
                              fontSize: "14px",
                              textAlign: "center",
                            }}
                          />
                          <button
                            style={{ width: 36, height: 36, borderRadius: 8, border: "1.5px solid #cbd5e1", background: "#FFF", fontSize: 20, fontWeight: 700, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#1A3B6E" }}
                            onClick={() => setPrintBagCount(Math.min(10, (parseInt(printBagCount) || 1) + 1))}
                          >
                            +
                          </button>
                          <button
                            style={{ ...S.btnPrimary, padding: "4px 14px", fontSize: "13px" }}
                            onClick={() => {
                              printSheet(order, parseInt(printBagCount) || 1);
                              setPrintPanelId(null);
                              setPrintBagCount(1);
                            }}
                          >
                            Print
                          </button>
                          <button
                            style={{ ...S.btnSecondary, padding: "4px 10px", fontSize: "13px" }}
                            onClick={handleCancelPrint}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          style={{ ...S.btnSecondary, padding: "4px 12px", fontSize: "13px" }}
                          onClick={() => { setPrintPanelId(order.orderId); setPrintBagCount(1); }}
                        >
                          Print Pickup Sheet
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

          <div style={{ ...S.flexRowBetween, marginTop: "48px", borderTop: "2px solid #e2e8f0", paddingTop: "32px" }}>
            <h2 style={S.cardTitle}>Cycle Order Aggregate</h2>
            <div style={S.flexRow}>
              <select
                style={{ ...S.select, width: "200px" }}
                value={cycleId}
                onChange={(e) => setCycleId(e.target.value)}
              >
                {cycles.map((c) => (
                  <option key={c.cycleId} value={c.cycleId}>
                    {c.cycleId} ({c.status})
                  </option>
                ))}
              </select>
              <button
                style={S.btnPrimary}
                onClick={() => {
                  loadAggregate();
                  loadOrders();
                }}
              >
                Load
              </button>
              <button style={S.btnSecondary} onClick={() => exportCSV(null)}>
                Export All CSV
              </button>
              <button style={S.btnSecondary} onClick={exportOrdersCSV} disabled={orders.length === 0}>
                Export Orders CSV
              </button>
            </div>
          </div>

          <div style={S.card}>
            {loading && <p style={{ color: "#64748b" }}>Loading aggregate...</p>}
            {aggregate &&
              Object.keys(aggregate).length > 0 &&
              Object.entries(aggregate).map(([sid, sData]) => (
                <div key={sid} style={{ marginBottom: "24px" }}>
                  <div style={{ ...S.flexRowBetween, marginBottom: "8px" }}>
                    <h3 style={{ fontSize: "14px", color: "#1e293b", margin: 0 }}>
                  <span
                    style={S.badge(
                      sid === "costco"
                        ? "#f59e0b"
                        : sid === "donation"
                          ? "#8b5cf6"
                          : "#3b82f6",
                    )}
                  >
                    {sData.supplierName || sid}
                  </span>{" "}
                  {sData.totalItems || 0} items · {sData.totalCases || 0} cases
                </h3>
                <button
                  style={{
                    ...S.btnSecondary,
                    padding: "4px 10px",
                    fontSize: "12px",
                  }}
                  onClick={() => exportCSV(sid)}
                >
                  Export {sData.supplierName || sid} CSV
                </button>
              </div>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Item</th>
                    <th style={S.th}>Total Qty</th>
                    <th style={S.th}>Cases Needed</th>
                    <th style={S.th}>SKU</th>
                  </tr>
                </thead>
                <tbody>
                  {(sData.items || []).map((item) => (
                    <tr key={item.itemId}>
                      <td style={S.td}>{item.name || item.itemId}</td>
                      <td style={S.td}>
                        <strong>{item.totalUnits}</strong>
                      </td>
                      <td style={S.td}>{item.casesNeeded}</td>
                      <td style={S.td}>{item.krogerSku || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        {aggregate && Object.keys(aggregate).length === 0 && (
          <p style={{ color: "#94a3b8", fontSize: "13px" }}>
            No aggregate data for this cycle.
          </p>
        )}
      </div>
        </>
      )}
    </>
  );
}
